import { useLearningUiTranslation } from '@features/learning/shared/use-learning-ui-translation'
import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { ArrowLeft, Gamepad2, Images, LibraryBig } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from '@shared/ui/notification-context'
import { isDashboardActivationKeyActive } from '@services/firebase'
import { useFlashcardsState } from '@features/flashcards/state/useFlashcardsState'
import {
  getPlayableFlashcards,
  getSelectedFlashcards,
} from '@features/flashcards/shared/flashcard-selectors'
import { getFlashcardCardsQueryOptions } from '@features/flashcards/shared/flashcard-query'
import { FlashcardCardGrid } from './FlashcardCardGrid'
import { FlashcardSelectedDrawer } from './FlashcardSelectedDrawer'
import { useDashboardState } from '@features/dashboard'
import { normalizeFlashcardLocale } from '@features/flashcards/shared/flashcard-language'
import {
  AppDialog,
  AppEmptyState,
  AppErrorState,
  AppLoadingState,
  LmsButton,
} from '@shared/ui'
import './flashcard-collection.css'
import '../shared/flashcard-feature-sub-header.css'

const LazyFlashcardSlideshowDialog = lazy(() =>
  import('@features/flashcards/slideshow/FlashcardSlideshowDialog').then((module) => ({
    default: module.FlashcardSlideshowDialog,
  })),
)

const LazyFlashcardGameLauncher = lazy(() =>
  import('@features/flashcards/game/FlashcardGameLauncher').then((module) => ({
    default: module.FlashcardGameLauncher,
  })),
)

const LazyFlashcardGameScreen = lazy(() =>
  import('@features/flashcards/game/FlashcardGameScreen').then((module) => ({
    default: module.FlashcardGameScreen,
  })),
)

function FlashcardsDialogLoadingFallback({
  description,
  message,
  title,
}: {
  readonly description?: string
  readonly message: string
  readonly title: string
}) {
  return (
    <AppDialog description={description} onClose={() => {}} open title={title}>
      <AppLoadingState
        message={message}
        minHeight={180}
        title={title}
      />
    </AppDialog>
  )
}

function FlashcardsFullScreenLoadingFallback({
  message,
}: {
  readonly message: string
}) {
  const ui = useLearningUiTranslation()
  return (
    <AppDialog fullScreen onClose={() => {}} open title={ui("Preparing flashcard game")}>
      <AppLoadingState
        message={message}
        minHeight="60vh"
        title={ui("Preparing flashcard game")}
      />
    </AppDialog>
  )
}

export function FlashcardCollectionScreen() {
  const ui = useLearningUiTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { i18n } = useTranslation('translation')
  const { state: dashboardState } = useDashboardState()
  const { actions, state } = useFlashcardsState()
  const [isStartingGame, setIsStartingGame] = useState(false)
  const [isSelectedDrawerOpen, setIsSelectedDrawerOpen] = useState(false)
  const [isGameLauncherOpen, setIsGameLauncherOpen] = useState(false)

  const activeCategory = state.activeCategory
  const hasLoadedActiveCategoryCards = activeCategory
    ? Object.prototype.hasOwnProperty.call(state.cardsByCategoryId, activeCategory.id)
    : false
  const cardsQuery = useQuery({
    ...getFlashcardCardsQueryOptions(activeCategory?.id ?? 'unknown-category'),
    enabled: Boolean(activeCategory) && !hasLoadedActiveCategoryCards,
  })
  const isCardsPreparing =
    !state.errorMessage && (state.loading.cards || !hasLoadedActiveCategoryCards)
  const cards = useMemo(
    () => (activeCategory ? state.cardsByCategoryId[activeCategory.id] ?? [] : []),
    [activeCategory, state.cardsByCategoryId],
  )
  const selectedCards = useMemo(
    () =>
      getSelectedFlashcards({
        cards,
        selectedCardIds: state.selectedCardIds,
      }),
    [cards, state.selectedCardIds],
  )
  const previewableCards = useMemo(() => getPlayableFlashcards(cards), [cards])
  const selectedPreviewCards = useMemo(
    () => getPlayableFlashcards(selectedCards),
    [selectedCards],
  )
  const slideshowCards =
    selectedPreviewCards.length > 0 ? selectedPreviewCards : previewableCards
  const locale = normalizeFlashcardLocale(i18n.language)
  const hasPremiumAccess = isDashboardActivationKeyActive(
    dashboardState.myUser?.activation_key,
  )

  useEffect(() => {
    if (!activeCategory || !cardsQuery.data || hasLoadedActiveCategoryCards) {
      return
    }

    actions.setErrorMessage(null)
    actions.setCards(activeCategory.id, cardsQuery.data)
  }, [
    actions,
    activeCategory,
    cardsQuery.data,
    hasLoadedActiveCategoryCards,
  ])

  useEffect(() => {
    if (!activeCategory || hasLoadedActiveCategoryCards) {
      return
    }

    const isLoading = cardsQuery.isLoading || cardsQuery.isFetching

    actions.setCardsLoading(isLoading)

    if (isLoading) {
      actions.setErrorMessage(null)
    }
  }, [
    actions,
    activeCategory,
    cardsQuery.isFetching,
    cardsQuery.isLoading,
    hasLoadedActiveCategoryCards,
  ])

  useEffect(() => {
    if (!cardsQuery.error) {
      return
    }

    actions.setErrorMessage(
      cardsQuery.error instanceof Error
        ? cardsQuery.error.message
        : ui("Flashcard cards failed to load. Please try again."),
    )
    actions.setCardsLoading(false)
  }, [actions, cardsQuery.error, ui])

  useEffect(() => {
    if (cardsQuery.data || cardsQuery.error || !hasLoadedActiveCategoryCards) {
      return
    }

    actions.setCardsLoading(false)
  }, [actions, cardsQuery.data, cardsQuery.error, hasLoadedActiveCategoryCards])

  const handleStartGame = async (level: 'basic' | 'advanced' | 'pro') => {
    const invalidSelectedRange =
      state.selectedCardIds.length > 1 && state.selectedCardIds.length < 12

    if (invalidSelectedRange) {
      enqueueSnackbar(
        ui("Choose one selected card, at least twelve selected cards, or clear the collection to use the whole category."),
        {
          variant: 'warning',
        },
      )
      return
    }

    const candidateCards =
      selectedCards.length > 0 ? selectedCards : getPlayableFlashcards(cards)

    try {
      setIsStartingGame(true)
      const { createFlashcardGameSession } = await import(
        '@features/flashcards/game/session-factory'
      )
      const session = createFlashcardGameSession({
        ui,
        cards: candidateCards,
        hasActivationKey: hasPremiumAccess,
        level,
        locale,
        supportsSpeech:
          typeof window !== 'undefined' &&
          Boolean(
            (window as typeof window & {
              readonly SpeechRecognition?: unknown
              readonly webkitSpeechRecognition?: unknown
            }).SpeechRecognition ??
              (window as typeof window & {
                readonly SpeechRecognition?: unknown
                readonly webkitSpeechRecognition?: unknown
              }).webkitSpeechRecognition,
          ),
      })
      actions.startGameSession(session)
      setIsGameLauncherOpen(false)
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error
          ? error.message
          : ui("Failed to start the flashcard game."),
        {
          variant: 'error',
        },
      )
    } finally {
      setIsStartingGame(false)
    }
  }

  if (!activeCategory) {
    return null
  }

  return (
    <div className="flashcardCollectionSurface">
      <header className="flashcardFeatureSubHeader flashcardCollectionSubHeader">
        <div className="flashcardFeatureSubHeaderInner flashcardCollectionSubHeaderInner">
          <LmsButton
            aria-label={ui("Back to categories")}
            className="flashcardCollectionBackButton rounded-full"
            leadingIcon={<ArrowLeft aria-hidden="true" className="size-5" />}
            onClick={() => {
              actions.setActiveCategory(null)
            }}
            size="icon-lg"
            variant="ghost"
          >
            <span className="sr-only">{ui("Back to categories")}</span>
          </LmsButton>
          <h1 className="flashcardFeatureSubHeaderTitle flashcardCollectionSubHeaderTitle">
            {activeCategory.title ?? activeCategory.id}
          </h1>
          <div className="flashcardCollectionToolbar">
            <LmsButton
              disabled={isCardsPreparing || slideshowCards.length === 0}
              leadingIcon={<Images aria-hidden="true" className="size-4" />}
              onClick={() => {
                actions.openSlideshow(0)
              }}
              variant="outline"
            >
              {ui("Slide show")}</LmsButton>
            <LmsButton
              leadingIcon={<LibraryBig aria-hidden="true" className="size-4" />}
              onClick={() => {
                setIsSelectedDrawerOpen(true)
              }}
              variant="outline"
            >
              {ui('Collection ({{count}})', { count: selectedCards.length })}
            </LmsButton>
            <LmsButton
              className="flashcardCollectionPlayGameButton"
              disabled={isCardsPreparing || cards.length === 0}
              leadingIcon={<Gamepad2 aria-hidden="true" className="size-4" />}
              onClick={() => {
                setIsGameLauncherOpen(true)
              }}
              variant="accent"
            >
              {ui("Play game")}</LmsButton>
          </div>
        </div>
      </header>

      <div className="flashcardCollectionContent">
        {isCardsPreparing ? (
          <AppLoadingState
            message={ui("Keep this page open while we load the cards for this category.")}
            minHeight={260}
            title={ui("Preparing this flashcard set")}
          />
        ) : null}

        {!isCardsPreparing && state.errorMessage ? (
          <AppErrorState
            message={state.errorMessage}
            title={ui("We could not load this flashcard set")}
          />
        ) : null}

        {!isCardsPreparing &&
        hasLoadedActiveCategoryCards &&
        !state.errorMessage &&
        cards.length === 0 ? (
          <AppEmptyState
            message={ui("This category loaded successfully, but no published flashcards are available yet.")}
            minHeight={240}
            title={ui("No flashcards are available in this category")}
          />
        ) : null}

        {!isCardsPreparing && !state.errorMessage && cards.length > 0 ? (
          <FlashcardCardGrid
            cards={cards}
            locale={locale}
            onPreview={(cardId) => {
              const previewIndex = previewableCards.findIndex((card) => card.id === cardId)
              actions.openSlideshow(previewIndex >= 0 ? previewIndex : 0)
            }}
            onToggleSelected={(cardId) => {
              actions.toggleSelectedCard(cardId)
            }}
            profile={dashboardState.myUser}
            selectedCardIds={state.selectedCardIds}
          />
        ) : null}
      </div>

      <FlashcardSelectedDrawer
        onClear={() => {
          actions.clearSelectedCards()
        }}
        onClose={() => {
          setIsSelectedDrawerOpen(false)
        }}
        onRemoveCard={(cardId) => {
          actions.toggleSelectedCard(cardId)
        }}
        open={isSelectedDrawerOpen}
        selectedCards={selectedCards}
      />

      {state.slideshow.open ? (
        <Suspense
          fallback={
            <FlashcardsDialogLoadingFallback
              message={ui("Keep this dialog open while we prepare the selected cards.")}
              title={ui("Preparing flashcard slideshow")}
            />
          }
        >
          <LazyFlashcardSlideshowDialog
            activeIndex={state.slideshow.activeIndex}
            cards={slideshowCards}
            locale={locale}
            onChange={(activeIndex) => {
              actions.openSlideshow(activeIndex)
            }}
            onClose={() => {
              actions.closeSlideshow()
            }}
            open={state.slideshow.open}
            profile={dashboardState.myUser}
          />
        </Suspense>
      ) : null}

      {isGameLauncherOpen ? (
        <Suspense
          fallback={
            <FlashcardsDialogLoadingFallback
              description={ui("Choose a level after we prepare the game options for these flashcards.")}
              message={ui("Keep this dialog open while we prepare the level choices.")}
              title={ui("Preparing game levels")}
            />
          }
        >
          <LazyFlashcardGameLauncher
            canUsePremiumLevel={hasPremiumAccess}
            onClose={() => {
              setIsGameLauncherOpen(false)
            }}
            onStart={handleStartGame}
            open={isGameLauncherOpen}
            selectedCount={state.selectedCardIds.length}
          />
        </Suspense>
      ) : null}

      {isStartingGame ? (
        <FlashcardsFullScreenLoadingFallback message={ui("Keep this screen open while we prepare your flashcard game.")} />
      ) : null}

      {state.gameSession ? (
        <Suspense
          fallback={
            <FlashcardsFullScreenLoadingFallback message={ui("Keep this screen open while we prepare your flashcard game.")} />
          }
        >
          <LazyFlashcardGameScreen />
        </Suspense>
      ) : null}
    </div>
  )
}



